import React, {
  useState, useEffect, useCallback, useRef,
} from "react";

import { motion } from "framer-motion/dist/framer-motion";
import OutsideClickHandler from "react-outside-click-handler";
import { toast } from "react-toastify";

import { buildGlobalTestSpecListSubtitle } from "../Helper";
import SpecManagerSearch from "../SpecManagerSearch";
import GlobalSpecFilters from "./GlobalSpecFilters";
import GlobalSpecTable from "./GlobalSpecTable";

import { getGlobalSpecs } from "../../../../../../actions/specManager";

import "./GlobalTestSpecList.css";

/**
 * List view for global test spec.
 * Part of GlobalTestSpec feature - completely separate from product spec.
 * Pagination: uses next_page_link from globalspecs/ response; infinite scroll on bottom.
 */
const GlobalTestSpecList = ({
  scrollbarRef,
  onEditTest,
}) => {
  const [testFiles, setTestFiles] = useState([]);
  const [testNextPageLink, setTestNextPageLink] = useState("");
  const [testLoadingNewContent, setTestLoadingNewContent] = useState(false);
  const [testFilterFlag, setTestFilterFlag] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(true);
  const isMountedRef = useRef(true);
  const searchControllerRef = useRef(null);

  const fetchTestSpecs = useCallback(async (
    filter = "",
    search = "",
    scrollToTop = false,
    page = "1",
    signal,
  ) => {
    if (!isMountedRef.current) return;
    setLoading(true);
    if (scrollToTop && scrollbarRef?.current) {
      scrollbarRef.current.scrollToTop();
    }
    let abortSignal = signal;
    if (!signal) {
      if (searchControllerRef.current) {
        searchControllerRef.current.abort();
      }
      const controller = new AbortController();
      searchControllerRef.current = controller;
      abortSignal = controller.signal;
    }
    const payload = { filter, search, page };
    let result;
    try {
      result = await getGlobalSpecs(payload, abortSignal);
      if (!isMountedRef.current) return;
      if (!result.success && !result.canceled) {
        setTestFiles([]);
        setTestNextPageLink("");
        toast.error(result.message);
      }
      if (result.success && Array.isArray(result.result)) {
        const nextPageLink = result.pages.nextPageLink;
        setTestFiles(result.result);
        setTestNextPageLink(nextPageLink);
      }
    } catch (error) {
      if (!isMountedRef.current) return;
      setTestFiles([]);
      setTestNextPageLink("");
      toast.error("Failed to fetch test specs.");
    }
    if (isMountedRef.current && !result?.canceled) {
      setLoading(false);
    }
  }, [scrollbarRef]);

  useEffect(() => {
    isMountedRef.current = true;
    const controller = new AbortController();
    fetchTestSpecs("", "", true, "1", controller.signal);
    return () => {
      isMountedRef.current = false;
      controller.abort();
      if (searchControllerRef.current) {
        searchControllerRef.current.abort();
      }
    };
  }, [fetchTestSpecs]);

  const handleSearchChange = async (value) => {
    setSearchValue(value);
    try {
      await fetchTestSpecs(testFilterFlag, value, true);
    } catch (error) {
      toast.error("Failed to fetch test specs.");
    }
  };

  const handleFilterToggle = async (filter) => {
    const newFilter = filter === testFilterFlag ? "" : filter;
    setTestFilterFlag(newFilter);
    try {
      await fetchTestSpecs(newFilter, searchValue, true);
    } catch (error) {
      toast.error("Failed to fetch test specs.");
    }
  };

  const handlePageChangeScroll = async () => {
    if (!testNextPageLink || testLoadingNewContent || loading) return;
    setTestLoadingNewContent(true);
    if (scrollbarRef?.current?.scrollToBottom) {
      scrollbarRef.current.scrollToBottom();
    }
    const page = testNextPageLink.split("page=")[1]?.split("&")[0] || "1";
    try {
      const result = await getGlobalSpecs({
        filter: testFilterFlag,
        search: searchValue,
        page,
      });
      if (!isMountedRef.current) return;
      if (result.success && Array.isArray(result.result)) {
        const nextPageLink = result.pages.nextPageLink;
        setTestFiles((prev) => [...prev, ...result.result]);
        setTestNextPageLink(nextPageLink);
      } else {
        if (scrollbarRef?.current?.scrollTop !== undefined) {
          scrollbarRef.current.scrollTop -= 100;
        }
        if (!result.success) {
          toast.error(result.message);
        }
      }
    } catch (error) {
      if (!isMountedRef.current) return;
      if (scrollbarRef?.current?.scrollTop !== undefined) {
        scrollbarRef.current.scrollTop -= 100;
      }
      toast.error("Failed to fetch test specs.");
    }
    if (isMountedRef.current) {
      setTestLoadingNewContent(false);
    }
  };

  const handleEditSpecClick = (e, index, status) => {
    e.preventDefault();
    e.stopPropagation();
    const testName = testFiles[index]?.test;
    if (!testName) {
      toast.error("Failed to get test data.");
      return;
    }
    onEditTest(testName, status);
  };

  const tableData = testFiles.map((t) => {
    const normalizedStatus = t.spec_status === "specs_added" || t.spec_status === "added"
      ? "added"
      : "pending";
    let subtitle = "";
    if (normalizedStatus === "added") {
      // List payload already includes `standard_specs.countries_list`; subtitle should reflect
      // selected markets for all users who can view this list (not gated on special pesticides).
      subtitle = buildGlobalTestSpecListSubtitle(t, { showMrlCountryDetails: true });
    }
    return {
      identifier: {
        test: t.test,
        subtitle,
      },
      status: normalizedStatus,
      productCount: t.product_count ?? t.products_count ?? 0,
    };
  });

  return (
    <>
      <div className="GlobalTestSpecList__FiltersContainer">
        <OutsideClickHandler onOutsideClick={() => {}}>
          <div>
            <SpecManagerSearch
              flag="reports_spec"
              searchValue={searchValue}
              searchField="test"
              placeholder="Search tests..."
              updateShowFilters={() => {}}
              handleSearchChange={(value) => handleSearchChange(value)}
            />
          </div>
        </OutsideClickHandler>
        <GlobalSpecFilters
          filterFlag={testFilterFlag}
          handleFilterToggle={handleFilterToggle}
          handleCloseFilterClick={(e) => {
            e.stopPropagation();
            handleFilterToggle("");
          }}
          loading={loading}
        />
      </div>
      <motion.div layout className="GlobalTestSpecList__TableContainer">
        <GlobalSpecTable
          tableData={tableData}
          handleEditSpecClick={handleEditSpecClick}
          handlePageChangeScroll={handlePageChangeScroll}
          loadingNewContent={testLoadingNewContent}
          specManagerScrollbar={scrollbarRef}
          loading={loading}
          searchValue={searchValue}
        />
      </motion.div>
    </>
  );
};

export default GlobalTestSpecList;
